import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const provider = searchParams.get('provider');
  const orderRef = searchParams.get('order_ref');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    // Redsys redirect (URL_OK) — poll /api/redsys/status/{order_ref}
    if (provider === 'redsys' && orderRef) {
      const pollRedsys = async (attempts = 0) => {
        const maxAttempts = 8;
        try {
          const { data } = await axios.get(`${API}/redsys/status/${orderRef}`);
          if (data.payment_status === 'pagado') {
            setStatus('success');
            setPaymentData({ amount_total: Math.round((data.total || 0) * 100) });
            clearCart();
            return;
          }
          if (data.payment_status === 'fallido') {
            setStatus('error');
            return;
          }
          if (attempts >= maxAttempts) {
            // Customer landed here so URL_OK was reached, treat as success
            // even if the webhook hasn't fired yet (rare network case).
            setStatus('success');
            setPaymentData({ amount_total: Math.round((data.total || 0) * 100) });
            clearCart();
            return;
          }
          setTimeout(() => pollRedsys(attempts + 1), 1500);
        } catch {
          if (attempts >= maxAttempts) setStatus('error');
          else setTimeout(() => pollRedsys(attempts + 1), 1500);
        }
      };
      pollRedsys();
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, provider, orderRef, clearCart]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" data-testid="checkout-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#C44536] animate-spin mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#2D231E] mb-2">Verificando Pago</h1>
            <p className="text-[#5C4D43]">Por favor espera mientras confirmamos tu pedido...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#2C5E3E] mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#2D231E] mb-2" data-testid="success-title">
              ¡Pago Exitoso!
            </h1>
            <p className="text-[#5C4D43] mb-6">
              Gracias por tu pedido. Hemos recibido tu pago de{' '}
              <span className="font-semibold text-[#C44536]">
                {paymentData ? `€${(paymentData.amount_total / 100).toFixed(2)}` : ''}
              </span>
            </p>
            <p className="text-sm text-[#5C4D43] mb-6">
              Recibirás un correo electrónico con los detalles de tu pedido.
            </p>
            <Link to="/">
              <Button 
                className="btn-primary px-8 py-3"
                data-testid="back-to-home-btn"
              >
                Volver al Inicio
              </Button>
            </Link>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <XCircle className="w-16 h-16 text-[#C44536] mx-auto mb-4" />
            <h1 className="font-serif text-2xl text-[#2D231E] mb-2">
              {status === 'expired' ? 'Sesión Expirada' : 'Error en el Pago'}
            </h1>
            <p className="text-[#5C4D43] mb-6">
              {status === 'expired' 
                ? 'Tu sesión de pago ha expirado. Por favor, intenta de nuevo.'
                : 'Hubo un problema al procesar tu pago. Por favor, intenta de nuevo.'}
            </p>
            <Link to="/">
              <Button 
                className="btn-primary px-8 py-3"
                data-testid="try-again-btn"
              >
                Volver al Inicio
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
